import { MAX_MONEY_DIGITS } from "./haklaiConstants";

const CLASSIC_TRAILING_DECIMAL_PATTERN = /\.0*$/;
const HAKLAI_AGORA = 0.01;
const HAKLAI_HALF_AGORA = HAKLAI_AGORA / 2;

export const parseHaklaiClassicMoneyInput = (
  rawValue,
  { maxDigits = MAX_MONEY_DIGITS } = {}
) => {
  const normalizedText = String(rawValue ?? "").replace(/[^\d.]/g, "");
  if (!normalizedText) return { value: null };

  const value = CLASSIC_TRAILING_DECIMAL_PATTERN.test(normalizedText)
    ? normalizedText
    : parseFloat(normalizedText);
  if (!Number.isFinite(value) && typeof value !== "string") return { value: null };

  const integerDigitCount = normalizedText.split(".")[0].length;
  if (Number.isFinite(maxDigits) && maxDigits > 0 && integerDigitCount > maxDigits) {
    return { skip: true };
  }
  return { value };
};

export const formatHaklaiClassicMoneyInput = (value, { empty = "" } = {}) => {
  if (value === null || value === undefined || value === "") return empty;
  if (typeof value === "string" && CLASSIC_TRAILING_DECIMAL_PATTERN.test(value)) {
    return value;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return empty;
  return numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export const normalizeHaklaiClassicMoneyInputOnBlur = (value) => (
  typeof value === "string" && CLASSIC_TRAILING_DECIMAL_PATTERN.test(value)
    ? value.replace(CLASSIC_TRAILING_DECIMAL_PATTERN, "")
    : value
);

export const formatHaklaiWholeMoney = (value, { empty = "" } = {}) => {
  if (value === null || value === undefined || value === "") return empty;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return empty;
  return Math.round(numericValue).toLocaleString("he-IL", {
    maximumFractionDigits: 0
  });
};

export const buildHaklaiPayableTaxDisplayByPropertyKey = (
  rows = [],
  authoritativeTotal = 0,
  fieldName = "payableTax"
) => {
  const entries = (Array.isArray(rows) ? rows : []).map((row = {}) => {
    const propertyKey = typeof row.propertyKey === "string" ? row.propertyKey.trim() : "";
    if (!propertyKey) {
      throw new Error("Haklai whole-money display row is missing a property key");
    }
    const numericValue = Number(row[fieldName]);
    if (!Number.isFinite(numericValue)) {
      throw new Error(`Haklai whole-money display value is invalid for property key ${propertyKey}`);
    }
    return {
      propertyKey,
      numericValue,
      displayValue: Math.round(numericValue)
    };
  });

  const propertyKeys = new Set();
  entries.forEach(({ propertyKey }) => {
    if (propertyKeys.has(propertyKey)) {
      throw new Error(`Haklai whole-money display contains duplicate property key ${propertyKey}`);
    }
    propertyKeys.add(propertyKey);
  });

  if (!entries.length) return {};

  const displayByKey = () => entries.reduce((acc, entry) => {
    acc[entry.propertyKey] = entry.displayValue;
    return acc;
  }, {});
  const numericTotal = Number(authoritativeTotal);
  if (!Number.isFinite(numericTotal)) return displayByKey();
  const rawPropertyTotal = entries.reduce((sum, entry) => sum + entry.numericValue, 0);
  const floatingPointSlack = Number.EPSILON
    * Math.max(1, Math.abs(rawPropertyTotal), Math.abs(numericTotal))
    * Math.max(1, entries.length);
  // Each saved property amount is already rounded to an agora, and the
  // fallback deal summary is rounded once more. Half an agora per row, plus
  // one half-agora for that summary, is the largest honest gap. A wider gap
  // keeps each property on its own whole shekel.
  const allowedGap = HAKLAI_HALF_AGORA * (entries.length + 1);
  if (Math.abs(rawPropertyTotal - numericTotal) > allowedGap + floatingPointSlack) {
    return displayByKey();
  }
  const targetTotal = Math.round(numericTotal);
  const initialDisplayTotal = entries.reduce((sum, entry) => sum + entry.displayValue, 0);
  const residual = targetTotal - initialDisplayTotal;

  if (residual !== 0) {
    const adjustmentDirection = residual > 0 ? 1 : -1;
    const rankedEntries = [...entries].sort((first, second) => {
      const firstError = adjustmentDirection > 0
        ? first.numericValue - first.displayValue
        : first.displayValue - first.numericValue;
      const secondError = adjustmentDirection > 0
        ? second.numericValue - second.displayValue
        : second.displayValue - second.numericValue;
      if (firstError !== secondError) return secondError - firstError;
      return first.propertyKey.localeCompare(second.propertyKey);
    });

    for (let index = 0; index < Math.abs(residual); index += 1) {
      rankedEntries[index % rankedEntries.length].displayValue += adjustmentDirection;
    }
  }

  return displayByKey();
};
